import { useState, useCallback, useEffect, useRef, useContext, useMemo } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import {
  CalendarEvent,
  EventsByDate,
  sortEventsByTime,
  moveEventToDate,
  getHorasPrevistasForDay,
} from '../utils/eventUtils';
import { AuthContext } from 'context/AuthContext';
import { parseJwt } from 'utils/parse';
import { AgendaService } from 'services/reports/endpoints/AgendaService';

interface UseCalendarEventsParams {
  initialEvents?: EventsByDate;
  onDragEnd?: (eventId: string, targetDate: string, event: CalendarEvent) => void;
  onReagendarTarefa?: (eventId: string, targetDate: string, event: CalendarEvent) => Promise<void>;
  fetchAgendaTasks?: (isWeekView?: boolean) => Promise<void>;
  isWeekView?: boolean;
}

interface UseCalendarEventsReturn {
  events: EventsByDate;
  setEvents: React.Dispatch<React.SetStateAction<EventsByDate>>;
  horasPrevistasByDate: Record<string, string>;
  handleDragEnd: (activeId: string, overId: string | null) => void;
  handleDragEndEvent: (event: DragEndEvent) => void;
  addEvent: (date: string, event: CalendarEvent) => void;
  removeEvent: (date: string, eventId: string) => void;
  updateEvent: (date: string, eventId: string, updates: Partial<CalendarEvent>) => void;
}

export const useCalendarEvents = (params?: UseCalendarEventsParams): UseCalendarEventsReturn => {
  const { authContext } = useContext(AuthContext);
  const [events, setEvents] = useState<EventsByDate>(params?.initialEvents || {});
  const onDragEndRef = useRef(params?.onDragEnd);
  const onReagendarTarefaRef = useRef(params?.onReagendarTarefa);
  const fetchAgendaTasksRef = useRef(params?.fetchAgendaTasks);
  const isWeekViewRef = useRef(params?.isWeekView);

  useEffect(() => {
    onDragEndRef.current = params?.onDragEnd;
    onReagendarTarefaRef.current = params?.onReagendarTarefa;
    fetchAgendaTasksRef.current = params?.fetchAgendaTasks;
    isWeekViewRef.current = params?.isWeekView;
  }, [params?.onDragEnd, params?.onReagendarTarefa, params?.fetchAgendaTasks, params?.isWeekView]);

  const handleReagendarTarefa = useCallback(
    async (eventId: string, targetDate: string, event: CalendarEvent) => {
      if (onReagendarTarefaRef.current) {
        await onReagendarTarefaRef.current(eventId, targetDate, event);
        return;
      }

      try {
        if (!authContext?.token) {
          return;
        }

        const { nameid: userId } = parseJwt(authContext.token);
        if (!userId) {
          return;
        }

        const tarefasIdsArray = event.tarefasIds || [];

        if (tarefasIdsArray.length === 0) {
          alert('Esta tarefa não pode ser reagendada pois não possui IDs válidos.');
          return;
        }

        const [hours, minutes] = event.time.split(':').map(Number);
        const [year, month, day] = targetDate.split('-').map(Number);
        const targetDateObj = new Date(year, month - 1, day, hours, minutes, 0, 0);

        const novaDataHora = targetDateObj.toISOString();

        await AgendaService.reagendarTarefas({
          usuarioId: Number(userId),
          tarefasIds: tarefasIdsArray,
          novaDataHora,
        });

        if (fetchAgendaTasksRef.current) {
          await fetchAgendaTasksRef.current(isWeekViewRef.current);
        }
      } catch (error) {}
    },
    [authContext?.token],
  );

  const handleDragEnd = useCallback(
    (activeId: string, overId: string | null) => {
      if (!overId || activeId === overId) {
        return;
      }

      let movedEvent: CalendarEvent | undefined;
      setEvents(prev => {
        Object.values(prev).forEach(dayEvents => {
          const found = dayEvents.find(e => e.id === activeId);
          if (found) {
            movedEvent = found;
          }
        });

        const newEvents = moveEventToDate(prev, activeId, overId);

        if (movedEvent) {
          setTimeout(() => {
            if (onDragEndRef.current) {
              onDragEndRef.current(activeId, overId, movedEvent!);
            } else {
              handleReagendarTarefa(activeId, overId, movedEvent!);
            }
          }, 0);
        }

        return newEvents;
      });
    },
    [handleReagendarTarefa],
  );

  const handleDragEndEvent = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      handleDragEnd(active.id as string, over?.id ? (over.id as string) : null);
    },
    [handleDragEnd],
  );

  const addEvent = useCallback((date: string, event: CalendarEvent) => {
    setEvents(prev => {
      const dayEvents = prev[date] || [];
      const updatedEvents = [...dayEvents, event];
      return {
        ...prev,
        [date]: sortEventsByTime(updatedEvents),
      };
    });
  }, []);

  const removeEvent = useCallback((date: string, eventId: string) => {
    setEvents(prev => {
      const dayEvents = prev[date] || [];
      return {
        ...prev,
        [date]: dayEvents.filter(e => e.id !== eventId),
      };
    });
  }, []);

  const updateEvent = useCallback((date: string, eventId: string, updates: Partial<CalendarEvent>) => {
    setEvents(prev => {
      const dayEvents = prev[date] || [];
      const updatedEvents = dayEvents.map(e => (e.id === eventId ? { ...e, ...updates } : e));
      return {
        ...prev,
        [date]: sortEventsByTime(updatedEvents),
      };
    });
  }, []);

  useEffect(() => {
    if (params?.initialEvents) {
      setEvents(params.initialEvents);
    }
  }, [params?.initialEvents]);

  const horasPrevistasByDate = useMemo(() => {
    const byDate: Record<string, string> = {};
    Object.entries(events).forEach(([dateKey, dayEvents]) => {
      byDate[dateKey] = getHorasPrevistasForDay(dayEvents);
    });
    return byDate;
  }, [events]);

  return {
    events,
    setEvents,
    horasPrevistasByDate,
    handleDragEnd,
    handleDragEndEvent,
    addEvent,
    removeEvent,
    updateEvent,
  };
};
